<template>
  <Teleport to="body">
    <Transition appear name="support-choices">
      <section
        ref="panel"
        class="support-choices-menu outline-none"
        role="dialog"
        :aria-label="showTicketForm ? 'Ticket eröffnen' : 'Support'"
        tabindex="-1"
        @keydown.esc.stop.prevent="$emit('close')"
      >
        <SupportTicketForm
          v-if="showTicketForm"
          v-model="ticketBody"
          v-model:attachments="ticketAttachments"
          @back="backToChoices"
          @close="$emit('close')"
        />
        <div v-show="!showTicketForm" class="support-choices-body">
          <header class="support-choices-header">
            <h2 class="support-choices-greeting">
              <span class="support-choices-hello">Hi{{ firstName ? ` ${firstName}` : '' }} 👋</span>
              <span>Wie können wir helfen?</span>
            </h2>
          </header>
          <div class="support-choices-content">
            <button
              type="button"
              class="support-choices-card"
              aria-label="Mit KI chatten"
              @click="$emit('chat')"
            >
              <div class="support-choice-copy">
                <div class="support-choice-title">
                  <h3>Mit KI chatten</h3>
                  <span class="support-choice-badge">Beliebt</span>
                </div>
                <p>Aktuell werden 85 % der Fragen <strong>in Echtzeit</strong> gelöst.</p>
              </div>
              <img class="support-choice-avatar" src="/assets/images/support-ai.png" alt="" />
            </button>
            <button
              ref="ticketButton"
              type="button"
              class="support-choices-card"
              aria-label="Ticket eröffnen"
              @click="showTicketForm = true"
            >
              <div class="support-choice-copy">
                <h3>Ticket eröffnen</h3>
                <p>Unser Team meldet sich <strong>innerhalb eines Werktags</strong></p>
              </div>
              <div class="support-choice-team" aria-hidden="true">
                <img class="support-choice-avatar" src="/assets/images/support-linda.jpeg" alt="" />
                <img class="support-choice-avatar" src="/assets/images/support-david.jpg" alt="" />
              </div>
            </button>
            <button
              type="button"
              class="support-choices-card"
              aria-label="Wissensdatenbank durchsuchen"
              @click="$emit('help')"
            >
              <div class="support-choice-copy">
                <h3>Wissensdatenbank durchsuchen</h3>
                <p>Anleitungen zum Nachlesen</p>
              </div>
              <MagnifyingGlassIcon class="support-choice-search" aria-hidden="true" />
            </button>
          </div>
        </div>
        <button
          v-if="!showTicketForm"
          type="button"
          class="absolute right-6 top-6 z-10 flex h-12 w-12 items-center justify-center rounded-full text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          :aria-label="$t('message.close')"
          @click="$emit('close')"
        >
          <ZeitIcon name="XMark" class="h-5 w-5" aria-hidden="true" />
        </button>
      </section>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import ZeitIcon from '@/components/ui/ZeitIcon.vue';
import SupportTicketForm from './SupportTicketForm.vue';

const props = defineProps({
  name: { type: String, default: '' },
});
defineEmits(['close', 'chat', 'help']);

const firstName = computed(() => props.name.trim().split(/\s+/)[0]);
const panel = ref<HTMLElement | null>(null);
const ticketButton = ref<HTMLButtonElement | null>(null);
const showTicketForm = ref(false);
const ticketBody = ref('');
const ticketAttachments = ref<File[]>([]);
let previousFocus: HTMLElement | null = null;

async function backToChoices() {
  showTicketForm.value = false;
  await nextTick();
  ticketButton.value?.focus({ preventScroll: true });
}

onMounted(() => {
  previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  panel.value?.focus({ preventScroll: true });
});

onBeforeUnmount(() => {
  if (panel.value?.contains(document.activeElement) && previousFocus?.isConnected) {
    previousFocus.focus({ preventScroll: true });
  }
});
</script>

<style scoped>
.support-choices-menu {
  position: fixed;
  z-index: 2147483002;
  right: 40px;
  bottom: 40px;
  width: 400px;
  max-width: calc(100vw - 80px);
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);
  max-height: 704px;
  overflow: hidden;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 5px 40px rgba(9, 14, 21, 0.24);
  transform-origin: bottom right;
}

.support-choices-body {
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.support-choices-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -55px 20px 20px;
}

.support-choices-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  overflow: hidden;
  border: 1px solid #ededed;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 28px rgba(9, 14, 21, 0.06), 0 1px 4px rgba(9, 14, 21, 0.06);
  font: 14px / 21px system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  text-align: left;
  cursor: pointer;
}

.support-choices-card:focus-visible {
  outline: 2px solid #df4d44;
  outline-offset: 2px;
}

.support-choice-copy {
  min-width: 0;
}

.support-choice-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.support-choices-card h3 {
  margin: 0;
  color: #14161a;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  transition: color 200ms;
}

.support-choices-card:hover h3,
.support-choices-card:focus-visible h3 {
  color: #df4d44;
}

.support-choices-card p {
  margin: 4px 0 0;
  color: #6c6f74;
}

.support-choices-card strong {
  color: #14161a;
  font-weight: 500;
}

.support-choice-badge {
  padding: 2px 12px;
  border-radius: 9999px;
  background: #a7f3d0;
  color: #14161a;
  font-weight: 500;
  line-height: 20px;
}

.support-choice-avatar {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.support-choice-team {
  display: flex;
  flex: none;
}

.support-choice-team .support-choice-avatar {
  border: 2px solid #fff;
}

.support-choice-team .support-choice-avatar + .support-choice-avatar {
  margin-left: -10px;
}

.support-choice-search {
  flex: none;
  width: 28px;
  height: 28px;
  color: #df4d44;
  stroke-width: 2.2;
}

.support-choices-header {
  position: relative;
  padding: 142px 40px 80px;
  background-image: url('/assets/images/support-header.jpg');
  background-size: 100% auto;
  background-position: left top;
  background-repeat: no-repeat;
}

.support-choices-header::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 80px;
  background: linear-gradient(transparent, #fff);
  pointer-events: none;
}

.support-choices-greeting {
  position: relative;
  z-index: 1;
  margin: 0;
  color: #fafafa;
  font: 600 28px / 34px system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  overflow-wrap: anywhere;
}

.support-choices-greeting span {
  display: block;
}

.support-choices-hello {
  opacity: 0.6;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
}

.support-choices-enter-active {
  transition: transform 300ms cubic-bezier(0, 1.2, 1, 1), opacity 83ms ease-out;
}

.support-choices-enter-from {
  transform: scale(0);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .support-choices-enter-active,
  .support-choices-card h3 {
    transition: none;
  }
}

@media (max-width: 480px) {
  .support-choices-header {
    background-size: cover;
  }

  .support-choices-menu {
    right: 16px;
    bottom: 16px;
    max-width: calc(100vw - 32px);
    height: calc(100vh - 32px);
    height: calc(100dvh - 32px);
  }
}
</style>
